import { TariffsStore } from '../stores/tariffs';
import { getCardSetsSelect } from './cardSets';
import { getCardsSelect } from './cards';
import { getCompaniesSelect } from './companies';
import { setUiTotal } from './ui';
import { IMeta, IRequestAPIBody, apiManager } from '../REST';
import {
    IBodyItemResponse,
    IParamsList,
    IPostItem,
    ITariffPackagesToBuyItem,
    ITariffPackagesToBuyItemRequestApi,
    ITariffPackagesToBuyListApi,
    ITariffPackagesToBuyListItem,
    ITariffPackagesToBuyListPaginated,
    ITariffToBuyItem,
    ITariffToBuyItemResponseApi,
    ITariffToBuyListApi,
    ITariffToBuyListItem,
    ITariffToBuyListPaginated,
} from '../interfaces';
import {
    IPackage,
    IPackageApi,
    IPackageApiItem,
    IPackageListPaginated,
    IPackagesList,
    IPostAssignPackage,
    IPostPackage,
    IPostPackageApi,
    IPostTariff,
    IPostTariffApi,
    ITariff,
    ITariffApi,
    ITariffApiItem,
    ITariffPackageInstance,
    ITariffPackageInstanceApi,
    ITariffPackageInstanceApiItem,
    ITariffStatistics,
    ITariffsApi,
    ITariffsList,
} from '../interfaces';
import { mapApi2UiField, mapUi2ApiField } from '../instruments';

// Main Tariff API functions
const formatTariffList = (list: ITariffApiItem[]): ITariffsList => {
    return list.map((tariff) => {
        const companyName = tariff.company?.name || '';
        const mapTariff = mapApi2UiField(tariff) as ITariff;
        return {
            ...mapTariff,
            price: mapTariff.priceCents ? mapTariff.priceCents / 100 : 0,
            companyName,
        } as ITariff;
    });
};

const setTariffsList = (list: ITariffsList) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setTariffsList(list);
};

export async function getTariffsList(
    params?: IParamsList,
    isLocal?: boolean,
): Promise<void | { list: ITariffsList; meta: IMeta }> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFFS', { searchParamsList: params });

    const response = resp as ITariffsApi;
    const list = formatTariffList(response.tariffs);
    if (!isError && !isLocal) {
        setTariffsList(list);
    } else if (isLocal) {
        return { list, meta: resp.meta as IMeta };
    }
}

// Get Tariff API function
const formatTariff = (tariff: ITariffApiItem): ITariff => {
    const companyName = tariff.company?.name || '';
    const tariffPackages = tariff.tariff_packages.map((value: ITariffApiItem) => mapApi2UiField(value));
    const mapTariff = mapApi2UiField(tariff) as ITariff;

    return {
        ...mapTariff,
        tariffPackages,
        companyName,
        price: mapTariff.priceCents ? mapTariff.priceCents / 100 : 0,
        cards: tariff.tariff_package_instances,
    } as ITariff;
};

const setTariff = (tariff: ITariff) => {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.setTariff(tariff);
};

export async function getTariff(id: string): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFF', { id });

    if (!isError) {
        const response = resp as ITariffApi;
        const tariff = formatTariff(response.tariff as unknown as ITariffApiItem);
        setTariff(tariff);
    }
}

export async function postTariff(body: IPostTariff): Promise<IPostItem> {
    console.log(Date.now(), '-(postTariff)->', { ...body }, `<-body-`);
    const parsedBody = mapUi2ApiField(body) as IPostTariffApi;

    const { isError, resp } = await apiManager.getApi('POST_TARIFF', {}, parsedBody as IRequestAPIBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function patchTariff(id: string, body: IPostTariff): Promise<IPostItem> {
    console.log(Date.now(), '-(patchTariff)->', { ...body }, `<-body-`);
    const parsedBody = mapUi2ApiField(body) as IPostTariffApi;

    const { isError, resp } = await apiManager.getApi('PATCH_TARIFF', { id }, parsedBody as IRequestAPIBody);
    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function replaceTariff(id: string, body: any): Promise<IBodyItemResponse | null> {
    const { resp, isError } = await apiManager.getApi('PUT_TARIFF', { id }, body);

    if (!isError && resp?.data) {
        return resp as IBodyItemResponse;
    }
    return null;
}

export async function deleteTariff(id: string): Promise<boolean> {
    const { isError } = await apiManager.getApi('DELETE_TARIFF', { id });

    return !isError;
}

// Tariff Package API functions
const formatTariffPackagesList = (list: IPackageApiItem[]): IPackagesList => {
    return list.map((tariffPackage) => {
        const resp = mapApi2UiField(tariffPackage);

        return {
            ...resp,
            company: resp.companyId as never as { name: string; id: string },
        } as IPackage;
    });
};

const setTariffsPackagesList = (list: IPackagesList) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setPackagesList(list);
};

export async function getTariffPackagesList(
    params?: IParamsList,
    isLocal?: boolean,
): Promise<void | IPackageListPaginated> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFF_PACKAGES', { searchParamsList: params });

    const response = resp as IPackageApi;
    const list = formatTariffPackagesList(response.tariff_packages);

    if (!isError && !isLocal) {
        setTariffsPackagesList(list);
    } else if (isLocal) {
        return {
            data: list,
            meta: response.meta,
        };
    }
}

const setTariffsPackage = (packageItem: IPackage) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setPackage(packageItem);
};

export async function getTariffPackage(id: string): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFF_PACKAGE', { id });

    const response = resp as IPostPackageApi;
    const packageItem = mapApi2UiField(response.tariff_package as unknown as IPackageApiItem) as IPackage;

    if (!isError) {
        setTariffsPackage({ ...packageItem, company: packageItem.companyId as never as { name: string; id: string } });
    }
}

export async function createTariffPackage(body: IPostPackage): Promise<IPostItem> {
    const parsedBody = mapUi2ApiField(body) as IRequestAPIBody;

    const { isError, resp } = await apiManager.getApi('POST_TARIFF_PACKAGE', {}, parsedBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function updateTariffPackage(id: string, body: IPostPackage): Promise<IPostItem> {
    const parsedBody = mapUi2ApiField(body) as IRequestAPIBody;

    const { isError, resp } = await apiManager.getApi('PATCH_TARIFF_PACKAGE', { id }, parsedBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;
    return { isError, errors, resp: response };
}

export async function replaceTariffPackage(id: string, body: any): Promise<IBodyItemResponse | null> {
    const { resp, isError } = await apiManager.getApi('PUT_TARIFF_PACKAGE', { id }, body);

    if (!isError && resp?.data) {
        return resp as IBodyItemResponse;
    }
    return null;
}

export async function deleteTariffPackage(id: string): Promise<boolean> {
    const { isError } = await apiManager.getApi('DELETE_TARIFF_PACKAGE', { id });
    return !isError;
}

export async function getTariffPackagesStatistics(): Promise<ITariffStatistics> {
    const { resp } = await apiManager.getApi('GET_TARIFF_PACKAGES_STATISTICS');

    return resp as ITariffStatistics;
}

// Tariff "To Buy" API functions (Read-only)
export async function getTariffsForPurchase(params?: IParamsList): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFFS_TO_BUY', { searchParamsList: params });

    if (!isError && resp?.data) {
        // You can create a separate setter for tariffs to buy if needed
        // setTariffsForPurchase(resp as IBodyListResponse);
    }
}

export async function getTariffForPurchase(id: string): Promise<IBodyItemResponse | null> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFF_TO_BUY', { id });

    if (!isError && resp?.data) {
        return resp as IBodyItemResponse;
    }
    return null;
}

export async function getTariffPackagesForPurchase(params?: IParamsList): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFFS_PACKAGES_TO_BUY', { searchParamsList: params });

    if (!isError && resp?.data) {
        // You can create a separate setter for tariff packages to buy if needed
        // setTariffPackagesForPurchase(resp as IBodyListResponse);
    }
}

export async function getTariffPackageForPurchase(id: string): Promise<IBodyItemResponse | null> {
    const { resp, isError } = await apiManager.getApi('GET_TARIFF_PACKAGE_TO_BUY', { id });

    if (!isError && resp?.data) {
        return resp as IBodyItemResponse;
    }
    return null;
}

// Tariff-Package Association API functions
export async function associateTariffWithPackage(body: IPostAssignPackage): Promise<IPostItem> {
    const parsedBody = mapUi2ApiField(body) as IRequestAPIBody;

    const { isError, resp } = await apiManager.getApi('POST_TARIFF_TARIFF_PACKAGE_ASSOCIATION', {}, parsedBody);

    const response = resp as IBodyItemResponse;
    const { errors } = response;

    return { isError, errors, resp: response };
}

export async function removeAssociationBetweenTariffAndPackage(id: string): Promise<boolean> {
    const { isError } = await apiManager.getApi('DELETE_TARIFF_TARIFF_PACKAGE_ASSOCIATION', { id });

    return isError;
}

const setTariffPackageInstanceList = (packageItemList: ITariffPackageInstance[]) => {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.setPackageInstanceItemList(packageItemList);
};

export async function getTariffPackageInstances(
    params?: IParamsList,
    isLocal?: boolean,
): Promise<boolean | IPackageListPaginated> {
    const { isError, resp } = await apiManager.getApi('GET_TARIFF_PACKAGE_INSTANCE', { searchParamsList: params });

    const response = resp as ITariffPackageInstanceApi;

    const tariffPackageItemList = response.tariff_package_instances.map((value) => {
        const mainParams = mapApi2UiField(value as unknown as ITariffPackageInstanceApiItem) as ITariffPackageInstance;

        mainParams.tariffPackage = mapApi2UiField(mainParams.tariffPackage as unknown as IPackageApiItem) as IPackage;
        mainParams.companyId = value.company.id.toString();
        mainParams.companyName = value.company.name;
        mainParams.company = value.company as never as { id: string; name: string };

        return {
            ...mainParams,
        };
    });

    if (isLocal) {
        return {
            meta: response.meta,
            data: tariffPackageItemList,
        };
    }

    response.meta && setUiTotal(response.meta.records_count);

    setTariffPackageInstanceList(tariffPackageItemList);

    return isError;
}

export function cleanTariffsStore(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.cleanPackage();
}

export function setTariffsForcedUpdateTime(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.setForcedUpdateTime();
}

export async function getTariffsSelect(params?: IParamsList): Promise<void> {
    const resp = (await getTariffsList({ ...params }, true)) as { list: ITariffsList; meta: IMeta };
    const TariffsStoreInstance = TariffsStore.getInstance();

    const selectData = resp.list.map((tariff: ITariff) => {
        return { id: tariff.id, name: tariff.name };
    });

    TariffsStoreInstance.setTariffsSelect(selectData, resp.meta.records_count);
}

export async function getTariffsPackageSelect(params?: IParamsList): Promise<void> {
    const packagesResp = (await getTariffPackagesList({ ...params }, true)) as IPackageListPaginated;
    const TariffsStoreInstance = TariffsStore.getInstance();

    const selectData = packagesResp.data.map((pkg: IPackage) => {
        return { id: pkg.id, name: pkg.name || pkg.id };
    });

    TariffsStoreInstance.setTariffsPackageSelect(selectData, packagesResp.meta.records_count);
}

export async function getTariffInstancesRelationSelect(companyId: string): Promise<void> {
    await getCardSetsSelect({ companyId }, true);
    await getCompaniesSelect(true);
    await getCardsSelect();
    await getTariffsSelect();
    await getTariffsPackageSelect();
}
// ------------ TariffToBuyList ------------
export function cleanTariffsToBuyListStore(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.cleanTariffsToBuyListStore();
}
export function setTariffsToBuyForcedUpdateTime(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.setForcedTariffsToBuyUpdateTime();
}
const setTariffToBuyList = (list: ITariffToBuyListItem[]) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setTariffsToBuyList(list);
};

export async function getTariffToBuyList(
    params?: IParamsList,
    isLocal?: boolean,
): Promise<void | ITariffToBuyListPaginated> {
    const { isError, resp } = await apiManager.getApi('GET_TARIFFS_TO_BUY', { searchParamsList: params });
    const response = resp as ITariffToBuyListApi;

    if (isError) {
        setUiTotal(0);
    } else if (!isLocal) {
        response.meta && setUiTotal(response.meta.records_count);
        const list = response.tariffs.map((t) => mapApi2UiField(t)) as ITariffToBuyListItem[];

        setTariffToBuyList(list);
    } else {
        const list = response.tariffs.map((t) => mapApi2UiField(t)) as ITariffToBuyListItem[];

        return {
            list,
            meta: response.meta,
        };
    }
}

const setTariffToBuyItem = (item: ITariffToBuyListItem) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setTariffsToBuyItem(item);
};
export async function getTariffToBuyItem(id: string): Promise<boolean> {
    const { isError, resp } = await apiManager.getApi('GET_TARIFF_TO_BUY', { id });
    const response = resp as ITariffToBuyItemResponseApi;
    if (!isError && response?.tariff) {
        const { tariff_packages, cards, company, ...infoApi } = response.tariff;
        const item = {
            ...mapApi2UiField(infoApi),
            tariffPackages: tariff_packages?.map((tp) => mapApi2UiField(tp)),
            cards,
            companyName: company?.name,
        } as ITariffToBuyItem;

        setTariffToBuyItem(item);
        return true;
    } else {
        return false;
    }
}

export const cleanTariffsToBuyItemStore = (): void => {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.cleanTariffsToBuyItemStore();
};

// ------------ TariffPackagesToBuyList ------------
export function cleanTariffsPackagesToBuyListStore(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.cleanStorePackagesToBuy();
}
export function setTariffsPackagesToByForcedUpdateTime(): void {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.setForcedUpdateTimePackagesToBuy();
}

const setTariffPackagesToBuyList = (list: ITariffPackagesToBuyListItem[]) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setTariffsListPackagesToBuy(list);
};

export async function getTariffPackagesToBuyList(
    params?: IParamsList,
    isLocal?: boolean,
): Promise<void | ITariffPackagesToBuyListPaginated> {
    const { isError, resp } = await apiManager.getApi('GET_TARIFFS_PACKAGES_TO_BUY', { searchParamsList: params });

    const response = resp as ITariffPackagesToBuyListApi;

    if (isError) {
        setUiTotal(0);
    } else if (!isLocal) {
        response.meta && setUiTotal(response.meta.records_count);
        const list = response.tariff_packages.map((t) => mapApi2UiField(t)) as ITariffPackagesToBuyListItem[];

        setTariffPackagesToBuyList(list);
    } else {
        const list = response.tariff_packages.map((t) => mapApi2UiField(t)) as ITariffPackagesToBuyListItem[];

        return {
            data: list,
            meta: response.meta,
        };
    }
}

const setTariffsPackagesToBuyItem = (item: ITariffPackagesToBuyItem) => {
    const StoreInstance = TariffsStore.getInstance();
    StoreInstance.setTariffsPackagesToBuyItem(item);
};
export async function getTariffPackagesToBuyItem(id: string): Promise<boolean> {
    const { isError, resp } = await apiManager.getApi('GET_TARIFF_PACKAGE_TO_BUY', { id });

    const response = resp as ITariffPackagesToBuyItemRequestApi;
    if (!isError && response?.tariff_package) {
        const { ...infoApi } = response.tariff_package;
        const item = {
            ...mapApi2UiField(infoApi),
        } as ITariffPackagesToBuyItem;

        setTariffsPackagesToBuyItem(item);
        return true;
    } else {
        return false;
    }
}

export const cleanTariffsPackagesToBuyItemStore = (): void => {
    const TariffsStoreInstance = TariffsStore.getInstance();
    TariffsStoreInstance.cleanTariffsPackagesToBuyItemStore();
};
